import { useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchTags, selectTagMap } from '../store/slices/tagsSlice';
import { fetchAccounts } from '../store/slices/accountsSlice';
import { fetchTransactions } from '../store/slices/transactionsSlice';
import AddTransactionFlow from '../components/AddTransactionFlow';
import TransactionList from '../components/TransactionList';
import TransactionRowSkeleton from '../components/skeletons/TransactionRowSkeleton';
import { useRouteScrollContainer } from '../hooks/useRouteScrollContainer';
import { useIsMobile } from '../hooks/useIsMobile';

const MonthHeaderSkeleton = () => (
  <div className="px-4 pb-1 pt-1">
    <div className="w-full">
      <div className="flex justify-between items-center mt-1 mb-1 px-1">
        <div className="h-4 w-28 animate-pulse rounded bg-muted dark:bg-muted-foreground/20" />
      </div>
    </div>
  </div>
);

const TransactionRowSkeletonItem = () => (
  <div className="px-4 pb-1 pt-1">
    <div className="w-full">
      <TransactionRowSkeleton />
    </div>
  </div>
);

function TransactionsScreen() {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const setSkeletonScrollContainer = useRouteScrollContainer<HTMLDivElement>('screen:transactions:skeleton');

  const { status: tagsStatus, error: tagsError } = useAppSelector((state) => state.tags);
  const { accounts, status: accountsStatus, error: accountsError } = useAppSelector((state) => state.accounts);
  const tagMap = useAppSelector(selectTagMap);
  const {
    transactions,
    status: transactionStatus,
    error: transactionError,
    hasMore,
    currentPage
  } = useAppSelector((state) => state.transactions);

  const isLoadingInitial = transactionStatus === 'loading' && transactions.length === 0;
  const isLoadingMore = transactionStatus === 'loadingMore';
  const overallError = tagsError || accountsError || transactionError;

  useEffect(() => {
    if (tagsStatus === 'idle') {
      dispatch(fetchTags());
    }
    if (accountsStatus === 'idle') {
      dispatch(fetchAccounts());
    }
    if (transactionStatus === 'idle' && currentPage === -1) {
      dispatch(fetchTransactions({ page: 0 }));
    }
  }, [dispatch, tagsStatus, accountsStatus, transactionStatus, currentPage]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || transactionStatus === 'loadingMore' || transactionStatus === 'failed') {
      return;
    }
    const nextPage = currentPage + 1;
    dispatch(fetchTransactions({ page: nextPage }));
  }, [dispatch, hasMore, transactionStatus, currentPage]);

  return (
    <div className="text-foreground flex flex-col justify-stretch h-full bg-background relative overflow-hidden">
      <div className="mx-auto flex h-full w-full max-w-lg flex-col">
        <div
          className="flex-shrink-0 px-4 py-2.5"
        >
          <div className="flex justify-between items-center px-1">
            <h1 className="text-3xl font-bold">Transactions</h1>
            <div className="flex">
              <AddTransactionFlow
                accounts={accounts}
                tagMap={tagMap}
              />
            </div>
          </div>
        </div>

        <div className='flex-1 min-h-0'>
          {isLoadingInitial && (
            <div ref={setSkeletonScrollContainer} className="overflow-y-auto">
              <MonthHeaderSkeleton />
              <TransactionRowSkeletonItem />
              <TransactionRowSkeletonItem />
              <TransactionRowSkeletonItem />
              <TransactionRowSkeletonItem />
              <TransactionRowSkeletonItem />
              <TransactionRowSkeletonItem />
              <TransactionRowSkeletonItem />
            </div>
          )}

          {!isLoadingInitial && !overallError && transactions.length === 0 && transactionStatus === 'succeeded' && (
            <div className="p-8 text-center text-muted-foreground">
              No transactions found.
            </div>
          )}
          {!isLoadingInitial && (
            <div className="h-full">
              <TransactionList
                transactions={transactions}
                className="h-full"
                useFullWidth={true}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                requireUserScrollBeforeLoadMore={!isMobile}
              />
            </div>
          )}

        </div>
      </div>

    </div>
  );
}

export default TransactionsScreen; 
